import { CustomColumnTypes } from "@/enum/list.enum";

import type { ListPerson } from "../../../../../schema/list";

export type DeepResearchStatus = "not_done" | "queued" | "running" | "failed" | "done";

/** What the page knows of the lead's latest run (PREC-4052): in flight, failed, or nothing. */
export type DeepResearchLiveState = "queued" | "running" | "failed" | null;

/** Minimal shape we read off the loosely-typed list column configs. */
type ListColumn = { type: CustomColumnTypes };

/** A list "uses" deep research when it has a DEEP_RESEARCH column configured. */
export const listHasDeepResearchColumn = (listColumns: ListColumn[] | undefined | null): boolean =>
	!!listColumns?.some((column) => column.type === CustomColumnTypes.DEEP_RESEARCH);

/**
 * Derives the deep research status for a lead.
 *
 * Research results live on the prospect, not the list, so a live state and `done` always show —
 * a lead can carry a finished research onto a list (e.g. a compose list) that has no
 * DEEP_RESEARCH column of its own. Only the idle `not_done` state is scoped to lists that
 * actually have a DEEP_RESEARCH column, to avoid noise where research was never set up.
 *
 * - `queued` / `running` — a run is waiting for a slot / handed to a worker (PREC-4052).
 * - `failed`   — the latest run failed; it wins over an older result it did not replace.
 *                All three are passed in by the caller because this util stays hook-free.
 * - `done`     — a non-empty research response is stored on the prospect.
 * - `not_done` — no result yet, on a list with a DEEP_RESEARCH column; `null` otherwise.
 */
export const getDeepResearchStatus = (
	row: ListPerson,
	live: DeepResearchLiveState
): DeepResearchStatus | null => {
	if (live) {
		return live;
	}

	const response = row.prospect?.deepResearchResponse?.response;
	const isDone = !!response && Object.keys(response).length > 0;

	if (isDone) {
		return "done";
	}

	return listHasDeepResearchColumn(row.list?.columns) ? "not_done" : null;
};
